import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, InputNumber } from 'antd';
import { BatteryHigh, House, MinusCircle, Plus, Sun } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import quoteServices, { type DealOpportunityProductAdder } from '../../../../services/oneButtonServices/quoteServices';
import { calculateAdderLineTotal, productTypeMatchesAdder } from './adderProductTotals';
import { OBDSelect } from './form-elements';
import './oneButton-css/AddersModal.css';

type AddersModalProps = {
    productTypes: string[];
    theme: 'dark' | 'light';
    opportunityId: string | number | null | undefined;
    opportunityDetails: any | null | undefined;
    opportunityProductAdders: DealOpportunityProductAdder[];
};

type AdderOption = {
    label: string;
    productTypeAdderId: number;
    productTypeId: number;
    unit: string;
    unitPrice: number;
    freeQuantity: number;
    isCustomAdder: boolean;
};

type AdderRow = {
    id: string;
    dealOpportunityProductAdderId: number;
    productTypeAdderId: number | '';
    productTypeId: number;
    qty: number;
    unit: string;
    unitPrice: number;
    freeQuantity: number;
    isCustomAdder: boolean;
    activeInd: boolean;
};

export type AddersModalHandle = {
    saveAdders: () => Promise<unknown>;
};

const money = (value: number) =>
    `$${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const emptyRow = (): AdderRow => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    dealOpportunityProductAdderId: 0,
    productTypeAdderId: '',
    productTypeId: 0,
    qty: 0,
    unit: '',
    unitPrice: 0,
    freeQuantity: 0,
    isCustomAdder: false,
    activeInd: true,
});

const calculateRowTotal = (row: AdderRow): number =>
    calculateAdderLineTotal(row.qty, row.unitPrice, row.freeQuantity);

const normalizeProductTypeName = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const productTypeMatches = productTypeMatchesAdder;

function productNavMeta(productType: string): { icon: React.ReactNode; tone: string; short: string } {
    const key = normalizeProductTypeName(productType);
    if (key.includes('solar')) {
        return { icon: <Sun size={18} weight="duotone" />, tone: 'solar', short: 'Solar' };
    }
    if (key.includes('battery')) {
        return { icon: <BatteryHigh size={18} weight="duotone" />, tone: 'battery', short: 'Battery' };
    }
    if (key.includes('roof') || key === 'r&r' || key === 'rr') {
        return { icon: <House size={18} weight="duotone" />, tone: 'roof', short: 'Roof' };
    }
    return {
        icon: <span className="obd-adders-nav__letter">{productType.charAt(0) || '?'}</span>,
        tone: 'default',
        short: productType,
    };
}

const AddersModal = forwardRef<AddersModalHandle, AddersModalProps>(
    ({ productTypes, theme, opportunityId, opportunityDetails, opportunityProductAdders }, ref) => {
        const productTypeAdders = useOneButtonStore((s) => s.productTypeAdders);
        const [removedProductAdders, setRemovedProductAdders] = useState<AdderRow[]>([]);

        const visibleProducts = useMemo(
            () =>
                productTypes
                    .map((productType) => productType.trim())
                    .filter(Boolean)
                    .map((productType) => {
                        const apiOptions = productTypeAdders
                            .filter((adder) => {
                                if (adder?.Active_Ind === false) return false;
                                return productTypeMatches(adder?.Product_Type, productType);
                            })
                            .map((adder) => ({
                                label: String(adder.Product_Type_Adder_Name ?? '').trim(),
                                productTypeAdderId: Number(adder.Product_Type_Adder_Id ?? 0),
                                productTypeId: Number(adder.Product_Type_Id ?? 0),
                                unit: String(adder.Unit ?? adder.Measure ?? '').trim(),
                                unitPrice: Number(adder.Unit_Price ?? 0),
                                freeQuantity: Number(adder.Free_Quantity ?? 0),
                                isCustomAdder: adder.Is_Custom_Adder === true,
                            }))
                            .filter((option) => option.label);

                        return {
                            key: productType,
                            label: productType,
                            options: apiOptions,
                        };
                    }),
            [productTypes, productTypeAdders],
        );

        const firstProductKey = visibleProducts[0]?.key ?? '';
        const [activeProductKey, setActiveProductKey] = useState(firstProductKey);
        const [rowsByProduct, setRowsByProduct] = useState<Record<string, AdderRow[]>>({});

        const opportunityProducts = useMemo(
            () => opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [],
            [opportunityDetails],
        );

        useEffect(() => {
            setActiveProductKey((current) =>
                visibleProducts.some((p) => p.key === current) ? current : firstProductKey,
            );
        }, [firstProductKey, visibleProducts]);

        useEffect(() => {
            const nextRows: Record<string, AdderRow[]> = {};

            visibleProducts.forEach((product) => {
                const productTypeIds = new Set(product.options.map((option) => option.productTypeId).filter(Boolean));
                const rows = (opportunityProductAdders ?? [])
                    .filter((adder) => adder?.Active_Ind !== false)
                    .filter((adder) => productTypeIds.has(Number(adder.Product_Type_Id ?? 0)))
                    .map((adder) => {
                        const option = product.options.find(
                            (opt) => opt.productTypeAdderId === Number(adder.Product_Type_Adder_Id ?? 0),
                        );
                        return {
                            id: `api-${adder.Deal_Opportunity_Product_Adder_Id}`,
                            dealOpportunityProductAdderId: Number(adder.Deal_Opportunity_Product_Adder_Id ?? 0),
                            productTypeAdderId: Number(adder.Product_Type_Adder_Id ?? 0),
                            productTypeId: Number(adder.Product_Type_Id ?? option?.productTypeId ?? 0),
                            qty: Math.floor(Number(adder.Quantity ?? 0)) || 0,
                            unit: option?.unit ?? '',
                            unitPrice: Number(adder.UnitPrice ?? option?.unitPrice ?? 0),
                            freeQuantity: Number(option?.freeQuantity ?? 0),
                            isCustomAdder: option?.isCustomAdder === true,
                            activeInd: true,
                        };
                    });

                nextRows[product.key] = rows.length > 0 ? rows : [emptyRow()];
            });

            setRowsByProduct(nextRows);
            setRemovedProductAdders([]);
        }, [opportunityProductAdders, visibleProducts]);

        const productTotals = useMemo(() => {
            const totals: Record<string, number> = {};
            visibleProducts.forEach((product) => {
                totals[product.key] = (rowsByProduct[product.key] ?? []).reduce(
                    (sum, row) => sum + calculateRowTotal(row),
                    0,
                );
            });
            return totals;
        }, [rowsByProduct, visibleProducts]);

        const grandTotal = useMemo(
            () => Object.values(productTotals).reduce((sum, value) => sum + value, 0),
            [productTotals],
        );

        const activeProduct =
            visibleProducts.find((product) => product.key === activeProductKey) ?? visibleProducts[0] ?? null;

        const updateRow = (product: string, rowId: string, patch: Partial<AdderRow>) => {
            setRowsByProduct((prev) => ({
                ...prev,
                [product]: (prev[product] ?? []).map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
            }));
        };

        const addRow = (product: string) => {
            setRowsByProduct((prev) => ({
                ...prev,
                [product]: [...(prev[product] ?? []), emptyRow()],
            }));
        };

        const deleteRow = (product: string, rowId: string) => {
            setRowsByProduct((prev) => {
                const removedRow = (prev[product] ?? []).find((row) => row.id === rowId);
                if (removedRow?.dealOpportunityProductAdderId) {
                    setRemovedProductAdders((current) => [
                        ...current.filter(
                            (row) => row.dealOpportunityProductAdderId !== removedRow.dealOpportunityProductAdderId,
                        ),
                        { ...removedRow, activeInd: false },
                    ]);
                }
                const nextRows = (prev[product] ?? []).filter((row) => row.id !== rowId);
                return {
                    ...prev,
                    [product]: nextRows.length > 0 ? nextRows : [emptyRow()],
                };
            });
        };

        const getOpportunityProductId = (productTypeId: number): number => {
            const row = (Array.isArray(opportunityProducts) ? opportunityProducts : []).find(
                (item: any) => Number(item?.Product_Type_Id ?? item?.product_Type_Id ?? 0) === Number(productTypeId),
            );
            return Number(row?.Opportunity_Product_Id ?? row?.opportunity_Product_Id ?? 0) || 0;
        };

        const setPayloadForAdders = () => {
            const activeAdders = Object.values(rowsByProduct).flat();
            return [...activeAdders, ...removedProductAdders]
                .filter((adder) => Number(adder.productTypeAdderId) > 0)
                .map((adder) => ({
                    deal_Opportunity_Product_Adder_Id: adder.dealOpportunityProductAdderId || 0,
                    opportunity_Id: Number(opportunityId ?? 0),
                    opportunity_Product_Id: getOpportunityProductId(adder.productTypeId || 0),
                    product_Type_Adder_Id: Number(adder.productTypeAdderId),
                    product_Type_Id: adder.productTypeId || 0,
                    quantity: Math.floor(Number(adder.qty) || 0),
                    unitPrice: Number(adder.unitPrice) || 0,
                    Active_Ind: adder.activeInd !== false,
                }));
        };

        useImperativeHandle(ref, () => ({
            saveAdders: () => quoteServices.DealSaveOpportunityProducttypesAdders(setPayloadForAdders()),
        }));

        if (!activeProduct) {
            return (
                <div className={`obd-adders-modal obd-theme-${theme}`}>
                    <p className="obd-adders-modal__empty">No product types available for adders.</p>
                </div>
            );
        }

        const rows = rowsByProduct[activeProduct.key] ?? [];
        const sectionTotal = productTotals[activeProduct.key] ?? 0;
        const navMeta = productNavMeta(activeProduct.label);

        return (
            <div className={`obd-adders-modal obd-theme-${theme}`}>
                <aside className="obd-adders-nav" aria-label="Product types">
                    <p className="obd-adders-nav__label">Products</p>
                    <div className="obd-adders-nav__list" role="tablist">
                        {visibleProducts.map((product) => {
                            const meta = productNavMeta(product.label);
                            const isActive = product.key === activeProduct.key;
                            return (
                                <button
                                    key={product.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`obd-adders-nav__item obd-adders-nav__item--${meta.tone}${
                                        isActive ? ' obd-adders-nav__item--active' : ''
                                    }`}
                                    onClick={() => setActiveProductKey(product.key)}
                                >
                                    <span className="obd-adders-nav__icon" aria-hidden>
                                        {meta.icon}
                                    </span>
                                    <span className="obd-adders-nav__copy">
                                        <span className="obd-adders-nav__name">{meta.short}</span>
                                        <span className="obd-adders-nav__sub">{money(productTotals[product.key] ?? 0)}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="obd-adders-nav__grand">
                        <span className="obd-adders-nav__grand-label">All products</span>
                        <strong className="obd-adders-nav__grand-value">{money(grandTotal)}</strong>
                    </div>
                </aside>

                <section
                    className={`obd-adders-workspace obd-adders-workspace--${navMeta.tone}`}
                    role="tabpanel"
                    aria-label={`${activeProduct.label} adders`}
                >
                    <header className="obd-adders-workspace__head">
                        <div className="obd-adders-workspace__title-wrap">
                            <span className="obd-adders-workspace__icon" aria-hidden>
                                {navMeta.icon}
                            </span>
                            <div>
                                <h3 className="obd-adders-workspace__title">{activeProduct.label}</h3>
                                <p className="obd-adders-workspace__hint">
                                    Line items billed per quantity · free qty applied when configured
                                </p>
                            </div>
                        </div>
                        <Button
                            type="primary"
                            className="obd-adders-workspace__add-btn"
                            icon={<Plus size={15} weight="bold" />}
                            onClick={() => addRow(activeProduct.key)}
                        >
                            Add line
                        </Button>
                    </header>

                    <div className="obd-adders-rows">
                        {rows.length === 0 ? (
                            <div className="obd-adders-rows__empty">No adders yet — add a line to get started.</div>
                        ) : (
                            rows.map((row, index) => {
                                const option = activeProduct.options.find(
                                    (opt) => opt.productTypeAdderId === row.productTypeAdderId,
                                );
                                const unitPrice = row.unitPrice || option?.unitPrice || 0;
                                const rowTotal = calculateRowTotal(row);

                                return (
                                    <article key={row.id} className="obd-adders-row">
                                        <span className="obd-adders-row__index" aria-hidden>
                                            {index + 1}
                                        </span>
                                        <div className="obd-adders-row__grid">
                                            <label className="obd-adders-field obd-adders-field--wide">
                                                <span className="obd-adders-field__label">Adder</span>
                                                <OBDSelect
                                                    value={row.productTypeAdderId || undefined}
                                                    options={activeProduct.options.map((opt) => ({
                                                        value: opt.productTypeAdderId,
                                                        label: opt.label,
                                                    }))}
                                                    placeholder="Select adder"
                                                    onChange={(value) => {
                                                        const selected = activeProduct.options.find(
                                                            (opt) => opt.productTypeAdderId === Number(value),
                                                        );
                                                        updateRow(activeProduct.key, row.id, {
                                                            productTypeAdderId: selected?.productTypeAdderId ?? '',
                                                            productTypeId: selected?.productTypeId ?? 0,
                                                            qty: 1,
                                                            unit: selected?.unit ?? '',
                                                            unitPrice: selected?.unitPrice ?? 0,
                                                            freeQuantity: selected?.freeQuantity ?? 0,
                                                            isCustomAdder: selected?.isCustomAdder === true,
                                                            activeInd: true,
                                                        });
                                                    }}
                                                    popupClassName={`obd-adders-select-dropdown obd-theme-${theme}`}
                                                />
                                            </label>
                                            <label className="obd-adders-field">
                                                <span className="obd-adders-field__label">Qty</span>
                                                <InputNumber
                                                    min={0}
                                                    precision={0}
                                                    value={row.qty}
                                                    className="obd-adders-field__input"
                                                    onChange={(value) =>
                                                        updateRow(activeProduct.key, row.id, {
                                                            qty: Math.floor(Number(value ?? 0)) || 0,
                                                        })
                                                    }
                                                />
                                            </label>
                                            <div className="obd-adders-field obd-adders-field--readonly">
                                                <span className="obd-adders-field__label">Unit</span>
                                                <span className="obd-adders-field__value">
                                                    {row.unit || option?.unit || '—'}
                                                </span>
                                            </div>
                                            <label className="obd-adders-field">
                                                <span className="obd-adders-field__label">Unit price</span>
                                                {row.isCustomAdder ? (
                                                    <InputNumber
                                                        min={0}
                                                        precision={2}
                                                        value={unitPrice}
                                                        className="obd-adders-field__input"
                                                        onChange={(value) =>
                                                            updateRow(activeProduct.key, row.id, {
                                                                unitPrice: Number(value ?? 0) || 0,
                                                            })
                                                        }
                                                        formatter={(value) => `$${value ?? 0}`}
                                                        parser={(value) =>
                                                            Number(String(value ?? '').replace(/[$,\s]/g, '')) as any
                                                        }
                                                    />
                                                ) : (
                                                    <span className="obd-adders-field__value">{money(unitPrice)}</span>
                                                )}
                                            </label>
                                            <div className="obd-adders-field obd-adders-field--total">
                                                <span className="obd-adders-field__label">Line total</span>
                                                <span className="obd-adders-field__total">{money(rowTotal)}</span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="obd-adders-row__delete"
                                            onClick={() => deleteRow(activeProduct.key, row.id)}
                                            aria-label={`Remove adder row ${index + 1}`}
                                        >
                                            <MinusCircle size={22} weight="fill" />
                                        </button>
                                    </article>
                                );
                            })
                        )}
                    </div>

                    <footer className="obd-adders-workspace__foot">
                        <span>Subtotal · {activeProduct.label}</span>
                        <strong>{money(sectionTotal)}</strong>
                    </footer>
                </section>
            </div>
        );
    },
);

export default AddersModal;
